import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { trackLeadSubmission } from '@/lib/analytics';
import { CreditCard, MessageCircle, Send } from 'lucide-react';

interface VehicleDetailLeadFormProps {
  vehicle: any;
  config: any;
  buttonColor: string;
  store: any;
  serviceType?: string;
  serviceTitle?: string;
  service?: any;
  variant?: 'default' | 'sidebar';
}

export function VehicleDetailLeadForm({
  vehicle,
  config,
  buttonColor,
  store,
  serviceType = 'message',
  serviceTitle = 'Envie uma mensagem',
  service,
  variant = 'default',
}: VehicleDetailLeadFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

  useEffect(() => {
    let vehicleInfo = '';

    if (serviceType === '65dcb7898eca108d87c91dbc') {
      vehicleInfo = `Olá,\n\nGostaria de simular um financiamento para o veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nPor favor, entrem em contato para mais informações sobre as condições de financiamento.`;
    } else {
      vehicleInfo = `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`;
    }

    setFormData((prev) => ({ ...prev, message: vehicleInfo }));
  }, [vehicle, serviceType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
  };

  const handlePhoneChange = (value: string) => {
    handleInputChange('phone', formatPhoneNumber(value));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({ title: '⚠️ Campo obrigatório', description: 'Por favor, preencha o campo Nome.', variant: 'destructive' });
      return false;
    }
    if (!formData.email.trim()) {
      toast({ title: '⚠️ Campo obrigatório', description: 'Por favor, preencha o campo E-mail.', variant: 'destructive' });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({ title: '❌ E-mail inválido', description: 'Por favor, insira um endereço de e-mail válido.', variant: 'destructive' });
      return false;
    }
    if (!formData.phone.trim()) {
      toast({ title: '⚠️ Campo obrigatório', description: 'Por favor, preencha o campo Telefone.', variant: 'destructive' });
      return false;
    }
    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 10) {
      toast({ title: '❌ Telefone inválido', description: 'Por favor, insira um número de telefone válido com DDD.', variant: 'destructive' });
      return false;
    }
    if (!formData.message.trim()) {
      toast({ title: '⚠️ Campo obrigatório', description: 'Por favor, preencha o campo Mensagem.', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const isApiType = service?.lead?.type === 'api' || service?.lead?.type === 'email';

      if (isApiType) {
        const { LEAD } = await import('@/lib/api');

        const leadData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''),
          message: formData.message,
          vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ' '}${vehicle.manufactureYear}/${vehicle.modelYear}`,
          id: vehicle.id,
          plaque: vehicle.plaque || '',
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version || '',
          year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
          price: formatPrice(vehicle.price),
          mileage: `${vehicle.mileage}km`,
        };

        const response = await LEAD(serviceType, store, leadData);

        if (response?.status == 200) {
          trackLeadSubmission({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            serviceType,
            vehicle: {
              id: vehicle.id,
              brand: vehicle.brand,
              model: vehicle.model,
              version: vehicle.version || '',
              year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
              price: vehicle.price,
              mileage: vehicle.mileage,
            },
          });

          toast({ title: 'Mensagem enviada!', description: 'Entraremos em contato em breve.' });

          setFormData({
            name: '',
            email: '',
            phone: '',
            message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`,
          });
        } else {
          toast({ title: 'Erro ao enviar', description: 'Tente novamente.', variant: 'destructive' });
        }
      } else {
        toast({ title: '✅ Redirecionando', description: 'Abrindo WhatsApp...' });

        const { openWhatsApp } = await import('@/lib/whatsapp');

        if (service?.lead?.ios) {
          let message = service.lead?.ios;
          const whatsappTemplate = message.substring(0, message.indexOf('&text=') + 6);
          message = message.substring(message.indexOf('&text=') + 6);

          const messageData: Record<string, string> = {
            nome: formData.name,
            name: formData.name,
            email: formData.email,
            telefone: formData.phone,
            phone: formData.phone,
            mensagem: formData.message,
            message: formData.message,
            veiculo: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            marca: vehicle.brand,
            brand: vehicle.brand,
            modelo: vehicle.model,
            model: vehicle.model,
            versao: vehicle.version || '',
            version: vehicle.version || '',
            ano: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            preco: formatPrice(vehicle.price),
            price: formatPrice(vehicle.price),
            km: `${vehicle.mileage}km`,
            mileage: `${vehicle.mileage}km`,
          };

          Object.entries(messageData).forEach(([key, value]) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            message = message.replace(regex, value);
          });

          const now = new Date();
          message = message.replace(/\{datahora\}/g, now.toLocaleString('pt-BR'));
          message = message.replace(/\{datetime\}/g, now.toLocaleString('pt-BR'));

          openWhatsApp(whatsappTemplate + encodeURIComponent(message));
        } else {
          const phoneMatch = store.whatsapp?.ios?.match(/phone=([^&]+)/);
          const phone = phoneMatch ? phoneMatch[1] : '';
          openWhatsApp(`whatsapp://send?phone=${phone}&text=${encodeURIComponent(formData.message)}`);
        }

        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`,
        });
      }
    } catch (error: any) {
      console.error('Error submitting lead:', error);
      toast({
        title: 'Erro ao enviar',
        description: error?.message || 'Ocorreu um erro ao enviar sua mensagem. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSidebar = variant === 'sidebar';

  return (
    <div className={isSidebar ? 'space-y-3' : 'space-y-4'}>
      <div className={isSidebar ? 'space-y-2.5' : 'space-y-3'}>
        {!isSidebar && (
          <CardTitle className="flex items-center gap-2">
            {serviceType === 'message' ? (
              <MessageCircle className="h-5 w-5 text-primary" />
            ) : (
              <CreditCard className="h-5 w-5 text-primary" />
            )}
            {serviceTitle}
          </CardTitle>
        )}

        <div className="space-y-2">
          <Label htmlFor="vd-lead-name" className={isSidebar ? 'text-xs font-medium' : 'text-sm font-semibold'}>
            Nome Completo <span className="text-red-500">*</span>
          </Label>
          <Input
            id="vd-lead-name"
            placeholder="Digite seu nome completo"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={isSidebar ? 'h-10' : 'h-11'}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="vd-lead-email" className={isSidebar ? 'text-xs font-medium' : 'text-sm font-semibold'}>
            E-mail <span className="text-red-500">*</span>
          </Label>
          <Input
            id="vd-lead-email"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={isSidebar ? 'h-10' : 'h-11'}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="vd-lead-phone" className={isSidebar ? 'text-xs font-medium' : 'text-sm font-semibold'}>
            Telefone <span className="text-red-500">*</span>
          </Label>
          <Input
            id="vd-lead-phone"
            type="tel"
            placeholder="(11) 99999-9999"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            maxLength={15}
            className={isSidebar ? 'h-10' : 'h-11'}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="vd-lead-message" className={isSidebar ? 'text-xs font-medium' : 'text-sm font-semibold'}>
            Mensagem <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="vd-lead-message"
            placeholder="Digite sua mensagem..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={isSidebar ? 3 : 5}
            className="resize-none"
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full transition-opacity duration-200 ${
            isSidebar ? 'h-11 text-sm font-semibold' : 'h-12 text-base font-semibold shadow-lg hover:shadow-xl'
          }`}
          style={{
            background: buttonColor,
            color: 'var(--wl7-on-button, #fff)',
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Enviar
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
